import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  TextField
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import SchoolIcon from "@mui/icons-material/School";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch
} from "react-hook-form";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  UserNotifications,
  useUpdateUser,
  useUpdateUserEmail,
  useUserById
} from "./userUtils";
import {
  ProgramMembership,
  ProgramRole,
  UpdateUserEmailArgs,
  UpdateUserInput,
  User
} from "../../API";
import { useUpdateMemberships } from "../Memberships/membershipUtils";
import {
  CONFIRM_SCOPED_ACCESS_REMOVAL,
  ScopedAccessRemoval
} from "../Memberships/membershipAPI";
import {
  ProgramMembershipMessages,
  ProgramRoleSelectFields,
  Regex,
  TraineeTypeSelectFields,
  UserFormErrors
} from "../../app/constants";
import {
  useTrainingStages,
  updateYearInStage,
  TrainingStage
} from "../TrainingStages/trainingStagesAPI";
import { LoadingButton } from "@mui/lab";
import { ReactHookFormTextField } from "../../common-ui/ReactHookFormTextField";
import {
  ReactHookFormSelectField,
  SelectMenuOption
} from "../../common-ui/ReactHookFormSelectField";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers-pro";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Role } from "../Auth/roles";
import { selectUserRoles } from "./usersSlice";
import { toEndDateIso } from "./bulkEndExecution";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { generateUUID } from "../../mocks/dataGenerators";
import { useAppSelector } from "../../app/hooks";
import {
  selectPrograms,
  selectProgramsStatus
} from "../Programs/programsSlice";
import { selectFeaturesForProgram } from "../Networks/networkFeaturesSlice";
import { useLoadFeaturesForProgram } from "../Networks/networkFeaturesUtils";
import { SIMPLColors } from "../../theme";
import { useProgramsIdentifierConfig } from "../Networks/networkUtils";
import { IdentifierConfigItem } from "../Networks/networksAPI";
import {
  IdentifierConfigStatus,
  IdentifierFields
} from "../Networks/IdentifierFields";
import {
  IDENTIFIER_KEY_LEARN,
  IDENTIFIER_KEY_NPI,
  IdentifierValues
} from "../Networks/identifierUtils";

const pageDetails: PageWrapperProps = {
  className: "EditUser"
};

type NewProgramRole = ProgramRole | "Clinician";

type YearInStageFieldProps = {
  control: any;
  formIndex: number;
  membershipField: { isNew?: boolean };
  stage: TrainingStage | undefined;
  disabledBase: boolean;
  setValue: any;
  trigger: any;
};

const YearInStageField = ({
  control,
  formIndex,
  membershipField,
  stage,
  disabledBase,
  setValue,
  trigger
}: YearInStageFieldProps) => {
  const { t } = useTranslation();
  const currentValue = useWatch({
    control,
    name: `programMemberships.${formIndex}.yearInStage`
  }) as number | null | undefined;

  // Show the dropdown for new memberships AND for existing memberships whose
  // yearInStage hasn't been set yet (i.e., null). This lets PAs set the initial
  // value directly from EditUser without waiting on a backfill pass.
  const yearShowDropdown = membershipField.isNew || currentValue == null;

  const yearAtMax =
    !stage || (currentValue != null && currentValue >= stage.maxYear);
  const bumpDisabled =
    disabledBase || currentValue == null || !stage || yearAtMax;

  return (
    <>
      {yearShowDropdown ? (
        <Grid xs={12} md={4} sx={{ width: "100%" }}>
          <ReactHookFormSelectField
            control={control}
            fieldName={`programMemberships.${formIndex}.yearInStage`}
            label="Clinical Training Year in Program"
            disabled={disabledBase || !stage}
            required={membershipField.isNew}
            choices={
              stage
                ? Array.from(
                    { length: stage.maxYear - stage.minYear + 1 },
                    (_, i) => stage.minYear + i
                  ).map((y) => ({ value: y, label: String(y) }))
                : []
            }
            trigger={trigger}
          />
        </Grid>
      ) : (
        <Grid xs={12} md={4} sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label={t("Clinical Training Year in Program")}
              value={currentValue == null ? "—" : String(currentValue)}
              sx={{ flex: 1 }}
              disabled
            />
            <Button
              variant="outlined"
              disabled={bumpDisabled}
              onClick={() => {
                if (currentValue != null) {
                  setValue(
                    `programMemberships.${formIndex}.yearInStage`,
                    currentValue + 1,
                    { shouldDirty: true, shouldTouch: true }
                  );
                  trigger("programMemberships");
                }
              }}
            >
              +1
            </Button>
          </Box>
        </Grid>
      )}
    </>
  );
};

type YearInStageStepperProps = {
  control: any;
  fieldName: string;
  stage: TrainingStage | undefined;
  disabled: boolean;
  trigger: any;
};

// Numeric stepper for a trainee's year-in-program. Whole numbers only, bounded
// by the stage's minYear/maxYear. Native up/down arrows plus direct entry; typed
// values are clamped into range.
const YearInStageStepper = ({
  control,
  fieldName,
  stage,
  disabled,
  trigger
}: YearInStageStepperProps) => {
  const { t } = useTranslation();
  const min = stage?.minYear;
  const max = stage?.maxYear;
  const clamp = (n: number) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { onChange, onBlur, value } }) => (
        <TextField
          type="number"
          size="small"
          value={value ?? ""}
          disabled={disabled || !stage}
          onBlur={onBlur}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(null);
            } else {
              const parsed = parseInt(raw, 10);
              if (isNaN(parsed)) return;
              onChange(clamp(parsed));
            }
            if (trigger) trigger("programMemberships");
          }}
          inputProps={{
            min,
            max,
            step: 1,
            inputMode: "numeric",
            "aria-label": t("Clinical Training Year in Program")
          }}
          sx={{ width: 96 }}
        />
      )}
    />
  );
};

// Resolves the role label + icon shown on each membership card.
// "Clinician" splits into "Clinician Trainee" / "Clinician Non-Trainee" based on
// the isClinicianTrainee form field.
type RoleDisplay = {
  label: string;
  Icon: React.ComponentType<any>;
  colorKey: "trainee" | "nonTrainee" | "programAdmin" | "admin" | "other";
};

const getRoleDisplay = (membership: MembershipPreview): RoleDisplay => {
  const role = membership.programRole as NewProgramRole;
  if (role === "Clinician") {
    if (membership.isClinicianTrainee === true) {
      return {
        label: "Clinician Trainee",
        Icon: SchoolIcon,
        colorKey: "trainee"
      };
    }
    return {
      label: "Clinician Non-Trainee",
      Icon: MedicalServicesIcon,
      colorKey: "nonTrainee"
    };
  }
  if (role === ProgramRole.Trainee) {
    return {
      label: "Clinician Trainee",
      Icon: SchoolIcon,
      colorKey: "trainee"
    };
  }
  if (role === ProgramRole.Attending) {
    return {
      label: "Clinician Non-Trainee",
      Icon: MedicalServicesIcon,
      colorKey: "nonTrainee"
    };
  }
  if (role === ProgramRole.ProgramAdmin) {
    return {
      label: "Program Admin",
      Icon: SupervisorAccountIcon,
      colorKey: "programAdmin"
    };
  }
  if (role === ProgramRole.Admin) {
    return {
      label: "Admin",
      Icon: AdminPanelSettingsIcon,
      colorKey: "admin"
    };
  }
  // Coach / RotationDirector (SE-1765): the select fields carry their
  // display names ("Rotation Director"), so the card never shows the enum.
  return {
    label:
      ProgramRoleSelectFields.find((f) => f.value === (role as string))
        ?.label ?? String(role ?? ""),
    Icon: SupervisorAccountIcon,
    colorKey: "other"
  };
};

// Role colors drawn from the SIMPL theme palette (see ../../theme.ts):
//   trainee      → Primary slate blue   (#4C6085) — the website's primary brand color
//   nonTrainee   → Secondary teal       (#6ABBCC) — visually distinct from trainee
//   programAdmin → Green                (#6BC584) — admin authority, non-alarming
//   admin        → Red                  (#C00000) — top-level admin, signals elevated privilege
const ROLE_BORDER_COLORS: Record<RoleDisplay["colorKey"], string> = {
  trainee: SIMPLColors.Primary,
  nonTrainee: SIMPLColors.Secondary,
  programAdmin: SIMPLColors.Green,
  admin: SIMPLColors.Red,
  other: SIMPLColors.Secondary
};

// Read-only card for an active, existing membership. Trainee memberships
// surface Trainee Type + Year (with +1 button). Other roles just show the
// program info + start date + End Membership action.
type ActiveMembershipCardProps = {
  membershipField: MembershipPreview;
  formIndex: number;
  membershipProgramName: string;
  membershipInstitutionName: string;
  stage: TrainingStage | undefined;
  control: any;
  setValue: any;
  trigger: any;
  editingUser: boolean;
  anyApiIsLoading: boolean;
  editingEmail: boolean;
  isUserCurrentlyAdministeringProgram: boolean;
  onEndMembership: (formIndex: number) => void;
};

const ActiveMembershipCard = ({
  membershipField,
  formIndex,
  membershipProgramName,
  membershipInstitutionName,
  stage,
  control,
  setValue,
  trigger,
  editingUser,
  anyApiIsLoading,
  editingEmail,
  isUserCurrentlyAdministeringProgram,
  onEndMembership
}: ActiveMembershipCardProps) => {
  const { t } = useTranslation();
  const roleDisplay = getRoleDisplay(membershipField);
  const isClinicianTrainee = membershipField.isClinicianTrainee === true;
  const yearInStage = useWatch({
    control,
    name: `programMemberships.${formIndex}.yearInStage`
  }) as number | null | undefined;

  const canEndMembership =
    editingUser && isUserCurrentlyAdministeringProgram;

  const startDateDisplay = membershipField.startDate
    ? format(new Date(membershipField.startDate), "PP")
    : "—";

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderLeft: `4px solid ${ROLE_BORDER_COLORS[roleDisplay.colorKey]}`,
        display: "flex",
        flexDirection: "column",
        gap: 1
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <roleDisplay.Icon
          fontSize="small"
          sx={{ color: ROLE_BORDER_COLORS[roleDisplay.colorKey] }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t(roleDisplay.label)}
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {membershipInstitutionName}
        {membershipInstitutionName && membershipProgramName ? " · " : ""}
        {membershipProgramName}
      </Typography>

      {isClinicianTrainee && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {t("Trainee Type")}:{" "}
            <Box component="span" sx={{ color: "text.primary" }}>
              {membershipField.traineeType ?? "—"}
            </Box>
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {editingUser ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t("Clinical Training Year in Program")}:
                </Typography>
                <YearInStageStepper
                  control={control}
                  fieldName={`programMemberships.${formIndex}.yearInStage`}
                  stage={stage}
                  disabled={anyApiIsLoading || editingEmail || !stage}
                  trigger={trigger}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("Clinical Training Year in Program")}:{" "}
                <Box component="span" sx={{ color: "text.primary" }}>
                  {stage && yearInStage != null
                    ? `${stage.yearLabelPrefix}-${yearInStage}`
                    : yearInStage != null
                    ? String(yearInStage)
                    : "—"}
                </Box>
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("Since")} {startDateDisplay}
        </Typography>
        {canEndMembership && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventBusyIcon />}
            disabled={anyApiIsLoading || editingEmail}
            onClick={() => onEndMembership(formIndex)}
          >
            {t("End Membership")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

// One row of the active memberships table view. Mirrors the data shown on the
// ActiveMembershipCard but laid out as a table row. Each row uses useWatch to
// stay reactive to yearInStage changes (the +1 button updates form state).
type ActiveMembershipTableRowProps = {
  membershipField: MembershipPreview;
  formIndex: number;
  membershipProgramName: string;
  membershipInstitutionName: string;
  stage: TrainingStage | undefined;
  control: any;
  setValue: any;
  trigger: any;
  editingUser: boolean;
  anyApiIsLoading: boolean;
  editingEmail: boolean;
  isUserCurrentlyAdministeringProgram: boolean;
  onEndMembership: (formIndex: number) => void;
};

const ActiveMembershipTableRow = ({
  membershipField,
  formIndex,
  membershipProgramName,
  membershipInstitutionName,
  stage,
  control,
  setValue,
  trigger,
  editingUser,
  anyApiIsLoading,
  editingEmail,
  isUserCurrentlyAdministeringProgram,
  onEndMembership
}: ActiveMembershipTableRowProps) => {
  const { t } = useTranslation();
  const roleDisplay = getRoleDisplay(membershipField);
  const isClinicianTrainee = membershipField.isClinicianTrainee === true;
  const yearInStage = useWatch({
    control,
    name: `programMemberships.${formIndex}.yearInStage`
  }) as number | null | undefined;

  const canEndMembership =
    editingUser && isUserCurrentlyAdministeringProgram;

  return (
    <TableRow hover>
      <TableCell sx={{ width: 36, pr: 0 }}>
        <roleDisplay.Icon
          fontSize="small"
          sx={{ color: ROLE_BORDER_COLORS[roleDisplay.colorKey] }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t(roleDisplay.label)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{membershipInstitutionName}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{membershipProgramName}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {isClinicianTrainee ? membershipField.traineeType ?? "—" : "—"}
        </Typography>
      </TableCell>
      <TableCell>
        {isClinicianTrainee ? (
          editingUser ? (
            <YearInStageStepper
              control={control}
              fieldName={`programMemberships.${formIndex}.yearInStage`}
              stage={stage}
              disabled={anyApiIsLoading || editingEmail || !stage}
              trigger={trigger}
            />
          ) : (
            <Typography variant="body2">
              {stage && yearInStage != null
                ? `${stage.yearLabelPrefix}-${yearInStage}`
                : yearInStage != null
                ? String(yearInStage)
                : "—"}
            </Typography>
          )
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {membershipField.startDate
            ? format(new Date(membershipField.startDate), "PP")
            : "—"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {canEndMembership && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventBusyIcon fontSize="small" />}
            disabled={anyApiIsLoading || editingEmail}
            onClick={() => onEndMembership(formIndex)}
          >
            {t("End")}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

// One row inside the Past Memberships accordion. Muted; Sys Admins additionally
// get an "Edit end date" action to change an already-ended date (SE-1724).
type PastMembershipRowProps = {
  membershipField: MembershipPreview;
  membershipProgramName: string;
  membershipInstitutionName: string;
  stage: TrainingStage | undefined;
  isSysAdmin: boolean;
  onEditEndDate: () => void;
  onRemoveEndDate: () => void;
};

// Inline form for a pending NEW membership (isNew=true). Compact 2-column
// layout that progressively reveals trainee fields. Replaces the previous
// 6-column grid of free-floating inputs.
type NewMembershipFormProps = {
  membershipField: MembershipPreview;
  formIndex: number;
  control: any;
  getValues: any;
  setValue: any;
  trigger: any;
  isFormBusy: boolean;
  institutionsCurrentUserCanAdminister: any[];
  getProgramsInInstitution: (institutionId?: string) => any[] | undefined;
  stage: TrainingStage | undefined;
  programRoleSelectFields: { label: string; value: NewProgramRole }[];
  onRemove: (formIndex: number) => void;
};

const NewMembershipForm = ({
  membershipField,
  formIndex,
  control,
  getValues,
  setValue,
  trigger,
  isFormBusy,
  institutionsCurrentUserCanAdminister,
  getProgramsInInstitution,
  stage,
  programRoleSelectFields,
  onRemove
}: NewMembershipFormProps) => {
  const { t } = useTranslation();
  const watchedRole = useWatch({
    control,
    name: `programMemberships.${formIndex}.programRole`
  }) as NewProgramRole | undefined;
  const watchedIsTrainee = useWatch({
    control,
    name: `programMemberships.${formIndex}.isClinicianTrainee`
  }) as boolean | undefined;
  const watchedInstitutionId = useWatch({
    control,
    name: `programMemberships.${formIndex}.institutionId`
  }) as string | undefined;
  const watchedProgramId = useWatch({
    control,
    name: `programMemberships.${formIndex}.programId`
  }) as string | undefined;
  const isClinician = watchedRole === "Clinician";

  // SE-1883: per-program feature flags gate whether Coach /
  // RotationDirector appear in this row's role dropdown. Mirrors
  // NewUserInvitation: fetch on-demand for the row's picked programId
  // (cache-first; no-op for a programId already in the map), then read
  // the flags and filter. A row without a program picked yet defaults
  // to "off" — same as the invitation form's pre-selection behaviour.
  useLoadFeaturesForProgram(watchedProgramId);
  const programFeatures = useAppSelector(
    selectFeaturesForProgram(watchedProgramId ?? null)
  );
  // SE-2302: Coach / Rotation Director never for someone who is a Trainee
  // in this row's program.
  const allMemberships = useWatch({ control, name: "programMemberships" }) as
    | MembershipPreview[]
    | undefined;
  const isTraineeInProgram =
    !!watchedProgramId &&
    traineeProgramIds(allMemberships ?? []).has(watchedProgramId);
  const filteredRoleFields = React.useMemo(
    () =>
      programRoleSelectFields.filter((role) => {
        if (role.value === (ProgramRole.Coach as unknown as NewProgramRole)) {
          return programFeatures.coaching && !isTraineeInProgram;
        }
        if (
          role.value ===
          (ProgramRole.RotationDirector as unknown as NewProgramRole)
        ) {
          return programFeatures.rotations && !isTraineeInProgram;
        }
        return true;
      }),
    [
      programRoleSelectFields,
      programFeatures.coaching,
      programFeatures.rotations,
      isTraineeInProgram
    ]
  );

  const institutionChoices: SelectMenuOption[] =
    institutionsCurrentUserCanAdminister?.flatMap((institution) =>
      institution
        ? [{ value: institution.id ?? "", label: institution.name ?? "" }]
        : []
    ) ?? [];

  const programChoices: SelectMenuOption[] =
    (getProgramsInInstitution(watchedInstitutionId) ?? []).flatMap((program) =>
      program ? [{ value: program.id, label: program.name }] : []
    );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderStyle: "dashed",
        display: "flex",
        flexDirection: "column",
        gap: 1.5
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t("New Membership")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onRemove(formIndex)}
          disabled={isFormBusy}
          aria-label={t("Remove new membership")}
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Grid container spacing={1.5}>
        <Grid xs={12} md={6}>
          <ReactHookFormSelectField
            control={control}
            fieldName={`programMemberships.${formIndex}.institutionId`}
            label="Institution"
            disabled={isFormBusy}
            required
            choices={institutionChoices}
            trigger={trigger}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <ReactHookFormSelectField
            control={control}
            fieldName={`programMemberships.${formIndex}.programId`}
            label="Program"
            disabled={isFormBusy || !watchedInstitutionId}
            required
            choices={programChoices}
            trigger={trigger}
          />
        </Grid>

        <Grid xs={12} md={6}>
          <ReactHookFormSelectField
            control={control}
            fieldName={`programMemberships.${formIndex}.programRole`}
            label="Role"
            disabled={isFormBusy}
            required
            choices={filteredRoleFields}
            trigger={trigger}
          />
        </Grid>

        {isClinician && (
          <Grid xs={12} md={6}>
            <Controller
              control={control}
              name={`programMemberships.${formIndex}.isClinicianTrainee`}
              rules={{
                validate: (value) =>
                  value !== null && value !== undefined
                    ? true
                    : "This field is required."
              }}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <FormControl
                  required
                  error={!!error}
                  sx={{ width: "100%" }}
                >
                  <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                    {t(
                      "Is this user a trainee in your program about whom you would like to collect assessments?"
                    )}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      value === true ? "yes" : value === false ? "no" : ""
                    }
                    onChange={(event) => {
                      onChange(event.target.value === "yes");
                      trigger("programMemberships");
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" disabled={isFormBusy} />}
                      label={t("Yes")}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" disabled={isFormBusy} />}
                      label={t("No")}
                    />
                  </RadioGroup>
                  {error && (
                    <FormHelperText>{error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}

        {isClinician && watchedIsTrainee === true && (
          <>
            <Grid xs={12} md={6}>
              <ReactHookFormSelectField
                control={control}
                fieldName={`programMemberships.${formIndex}.traineeType`}
                label="Trainee Type"
                disabled={isFormBusy}
                required
                choices={TraineeTypeSelectFields}
                trigger={trigger}
              />
            </Grid>
            <YearInStageField
              control={control}
              formIndex={formIndex}
              membershipField={membershipField}
              stage={stage}
              disabledBase={isFormBusy}
              setValue={setValue}
              trigger={trigger}
            />
          </>
        )}
      </Grid>
    </Paper>
  );
};

const PastMembershipRow = ({
  membershipField,
  membershipProgramName,
  membershipInstitutionName,
  stage,
  isSysAdmin,
  onEditEndDate,
  onRemoveEndDate
}: PastMembershipRowProps) => {
  const { t } = useTranslation();
  const roleDisplay = getRoleDisplay(membershipField);
  const startStr = membershipField.startDate
    ? format(new Date(membershipField.startDate), "PP")
    : "?";
  const endStr = membershipField.endDate
    ? format(new Date(membershipField.endDate), "PP")
    : "—";
  const stageLabel =
    stage &&
    membershipField.yearInStage != null &&
    membershipField.isClinicianTrainee === true
      ? `${stage.yearLabelPrefix}-${membershipField.yearInStage}`
      : null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ py: 0.75, color: "text.secondary" }}
    >
      <roleDisplay.Icon fontSize="small" sx={{ opacity: 0.6 }} />
      <Typography
        variant="caption"
        sx={{ flex: 1, fontSize: "0.8125rem" }}
      >
        <Box component="span" sx={{ fontWeight: 500 }}>
          {roleDisplay.label}
        </Box>
        {" · "}
        {membershipInstitutionName} {membershipInstitutionName ? "—" : ""}{" "}
        {membershipProgramName}
        {membershipField.traineeType ? ` · ${membershipField.traineeType}` : ""}
        {stageLabel ? ` · ${stageLabel}` : ""}
        {" · "}
        {startStr} — {endStr}
      </Typography>
      {isSysAdmin && (
        <>
          <Button
            size="small"
            variant="text"
            onClick={onEditEndDate}
            sx={{ flexShrink: 0, minWidth: "auto", fontSize: "0.75rem" }}
          >
            {t("Edit end date")}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onRemoveEndDate}
            sx={{ flexShrink: 0, minWidth: "auto", fontSize: "0.75rem" }}
          >
            {t("Remove end date")}
          </Button>
        </>
      )}
    </Stack>
  );
};

// Stripping away nested objects to avoid circular references for react-hook-form
// Also ridding fields not necessary for display in the form.
export type MembershipPreview = Omit<
  ProgramMembership,
  "programMember" | "program" | "createdAt" | "updatedAt"
> & {
  isNew?: boolean;
  institutionId?: string;
  programRole: NewProgramRole;
  isClinicianTrainee?: boolean | undefined; // Radio state when role = Clinician
  yearInStage?: number | null;
  programTrainingStageId?: string | null;
};

const effectiveRole = (m: MembershipPreview): ProgramRole | undefined => {
  if (m.isClinicianTrainee) return ProgramRole.Trainee;
  if ((m.programRole as unknown as string) === "Clinician") {
    return ProgramRole.Attending;
  }
  return (m.programRole as ProgramRole) ?? undefined;
};

const isCoachOrDirectorRole = (role: NewProgramRole | null | undefined) =>
  role === ProgramRole.Coach || role === ProgramRole.RotationDirector;

/**
 * Programs where the (unsaved) form holds an active Trainee membership —
 * where Coach / Rotation Director may not be held.
 * update-program-memberships enforces the same.
 */
const traineeProgramIds = (memberships: MembershipPreview[]): Set<string> => {
  const trainee = new Set<string>();
  for (const m of memberships) {
    if (m.endDate || !m.programId) continue;
    if (effectiveRole(m) === ProgramRole.Trainee) trainee.add(m.programId);
  }
  return trainee;
};

export type EditUserInputs = {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  pgy: number | null;
  identifiers: IdentifierValues;
  suffix: string | null;
  programMemberships: MembershipPreview[];
  jobTitle?: string | null;
};

// The user's stored identifiers by key (get-user's `identifiers` map; falls back
// to the legacy npi / simplLearnId fields).
const storedIdentifierValues = (user?: User | null): IdentifierValues => {
  const map = (user as any)?.identifiers as IdentifierValues | undefined;
  if (map) return { ...map };
  const values: IdentifierValues = {};
  if (user?.npi != null) values[IDENTIFIER_KEY_NPI] = String(user.npi);
  if (user?.simplLearnId) values[IDENTIFIER_KEY_LEARN] = user.simplLearnId;
  return values;
};

const FELLOWSHIP_STAGE_CODE = "FELLOWSHIP";
const PGY_MIN = 1;
const PGY_MAX = 12;

type EditUserProps = {
  id: string;
};

export const EditUser = ({ id }: EditUserProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, status, error, getUserById } = useUserById(id);
  const {
    status: updateUserStatus,
    error: updateUserError,
    updateUser
  } = useUpdateUser();
  const [indexOfMembershipToEnd, setIndexOfMembershipToEnd] =
    useState<number>();
  const [showEndMembershipModal, setShowEndMembershipModal] = useState(false);
  // SE-2798: remove-end-date (reactivate) confirmation dialog state.
  const [showRemoveEndDateModal, setShowRemoveEndDateModal] = useState(false);
  const [indexToReactivate, setIndexToReactivate] = useState<number>();
  // SE-1724: the Sys-Admin-chosen end date (today or earlier); null = today.
  const [endDatePick, setEndDatePick] = useState<Date | null>(null);
  // SE-1724: validation error from the end-date picker. maxDate/minDate only
  // bound the CALENDAR; a date TYPED into the field still flows through onChange,
  // so we track the field's own validation (future / before-start / unparseable)
  // to block the confirm button. `null` = valid.
  const [endDateError, setEndDateError] = useState<string | null>(null);
  // SE-3027: ending a Rotation Director / Coach membership also removes that
  // person's directorships / coaching assignments in the program. The server
  // lists them on the first save; the admin confirms here, and the save is
  // re-sent with the confirmation.
  const [pendingScopedRemovals, setPendingScopedRemovals] = useState<
    ScopedAccessRemoval[] | null
  >(null);
  const confirmScopedRemovalRef = useRef(false);
  const [membershipsViewMode, setMembershipsViewMode] = useState<
    "cards" | "table"
  >("cards");
  const {
    status: updateEmailStatus,
    error: updateEmailError,
    updateUserEmail
  } = useUpdateUserEmail();
  const {
    status: updateMembershipsStatus,
    updateMemberships
  } = useUpdateMemberships();

  const {
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  const getProgramsInInstitution = (institutionId?: string) =>
    programsCurrentUserCanAdminister?.filter(
      (p) => p.institutionId == institutionId
    );

  const programs = useAppSelector(selectPrograms);
  const programsStatus = useAppSelector(selectProgramsStatus);
  // SE-1724: only Sys Admins may back-date an end or edit an already-ended date.
  const currentUserRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = currentUserRoles?.includes(Role.SystemAdmin) ?? false;
  const {
    trainingStages,
    status: trainingStagesStatus
  } = useTrainingStages();

  const stageForMembership = (
    membershipField: MembershipPreview,
    formIndex: number
  ): TrainingStage | undefined => {
    // For existing memberships, prefer the trainingStageId we loaded from get-user
    // (always fresh) over the cached `programs` Redux slice (which may be stale
    // if a program's training_stage was edited during this session).
    if (!membershipField.isNew && membershipField.programTrainingStageId) {
      return trainingStages.find(
        (s) => s.id === membershipField.programTrainingStageId
      );
    }
    const programId = membershipField.isNew
      ? getValues(`programMemberships.${formIndex}.programId`)
      : membershipField.programId;
    if (!programId) return undefined;
    const matchingProgram = programs?.find((p) => p.id === programId);
    const trainingStageId = (matchingProgram as any)?.trainingStageId;
    if (!trainingStageId) return undefined;
    return trainingStages.find((s) => s.id === trainingStageId);
  };

  const {
    getValues,
    handleSubmit,
    reset,
    trigger,
    setError,
    clearErrors,
    setValue,
    watch,
    control,
    formState: { errors, isDirty, dirtyFields }
  } = useForm<EditUserInputs>({
    defaultValues: {
      programMemberships: []
    },
    reValidateMode: "onChange"
  });

  // SE-3118: identifier fields come from the network config of the user's active
  // memberships (union across memberships; required = OR), so any identifier type
  // shows up without code changes. Config is authoritative: an identifier removed
  // from the network disappears here even if the user has a value (the value is
  // kept, just not shown or sent).
  const watchedMemberships = (watch("programMemberships") || []) as any[];
  // SE-2246 identifier visibility: only consider programs the VIEWER administers.
  // getProgramsCurrentUserCanAdminister returns all programs for a SysAdmin and
  // only the PA's programs for a Program Admin, so a PA sees only identifier
  // types used by networks they administer (never another network's), while a
  // SysAdmin sees everything.
  const viewerAdministeredProgramIds = new Set(
    (programsCurrentUserCanAdminister ?? []).map((p) => p.id)
  );
  const activeMemberProgramIds = watchedMemberships
    .filter(
      (m) =>
        m?.programId &&
        !m?.endDate &&
        viewerAdministeredProgramIds.has(m.programId)
    )
    .map((m) => m.programId as string);
  const {
    configs: identifierConfigs,
    status: identifierStatus,
    retry: retryIdentifierConfig
  } = useProgramsIdentifierConfig(activeMemberProgramIds);
  // Until the config loads (or if it fails) we can't tell which identifiers apply,
  // so the fields are replaced by a spinner / retry and Save is off.
  const identifierConfigLoading =
    activeMemberProgramIds.length > 0 && identifierStatus === "loading";
  const identifierConfigFailed =
    activeMemberProgramIds.length > 0 && identifierStatus === "failed";

  const applicableIdentifiers = new Map<string, IdentifierConfigItem>();
  for (const m of watchedMemberships) {
    if (!m?.programId || m?.endDate) continue;
    // Skip programs the viewer doesn't administer (SysAdmin administers all).
    if (!viewerAdministeredProgramIds.has(m.programId)) continue;
    const cfg = identifierConfigs[m.programId];
    if (!cfg) continue;
    // Memberships store clinicians as programRole "Clinician" + isClinicianTrainee;
    // the config keys on the real program roles, so resolve to Trainee/Attending
    // (mirrors the save path).
    const effectiveRole =
      (m.programRole as string) === "Clinician"
        ? m.isClinicianTrainee
          ? "Trainee"
          : "Attending"
        : m.programRole;
    for (const it of cfg.identifiersByRole[effectiveRole] || []) {
      const ex = applicableIdentifiers.get(it.key);
      if (ex) ex.required = ex.required || it.required;
      else applicableIdentifiers.set(it.key, { ...it });
    }
  }
  const identifierItems = Array.from(applicableIdentifiers.values());

  const {
    fields: membershipFields,
    append: appendMembership,
    remove: removeMembership,
    replace: replaceMemberships
  } = useFieldArray({
    name: "programMemberships",
    control,
    rules: {
      validate: {
        unique: (programMemberships) => {
          let twoMembershipsWithSameProgram = false;
          const activeMemberships = programMemberships.filter(
            (programMembership) => !programMembership.endDate
          );
          activeMemberships?.forEach((activeMembership, index) => {
            twoMembershipsWithSameProgram = activeMemberships.some(
              (innerValue, innerIndex) =>
                index !== innerIndex &&
                activeMembership.programId === innerValue.programId &&
                activeMembership.programRole === innerValue.programRole
            );
          });
          return (
            !twoMembershipsWithSameProgram ||
            UserFormErrors.DuplicateProgramMembership
          );
        },
        oneTraineeMembership: (programMemberships) => {
          const activeTraineeMembershipCount = programMemberships.filter(
            (membership) => !membership.endDate && membership.isClinicianTrainee
          ).length;
          return (
            activeTraineeMembershipCount <= 1 ||
            UserFormErrors.OneTraineeMembership
          );
        },
        traineeTypeRequired: (programMemberships) => {
          const isTraineeTypeEmpty = (traineeType: any) => {
            return (
              !traineeType ||
              traineeType === "" ||
              traineeType === null ||
              traineeType === undefined
            );
          };

          const missingTraineeType = programMemberships.find(
            (membership) =>
              !membership.endDate &&
              membership.isNew &&
              membership.isClinicianTrainee === true &&
              isTraineeTypeEmpty(membership.traineeType)
          );
          return !missingTraineeType || "Trainee Type is required for trainees";
        },
        programRoleRequiredForNew: (programMemberships) => {
          const missingRole = programMemberships.find(
            (membership) =>
              !membership.endDate &&
              membership.isNew &&
              (membership.programRole == null ||
                (membership.programRole as unknown as string) === "")
          );
          return !missingRole || "Role is required";
        },
        yearInStageRequiredForNewTrainees: (programMemberships) => {
          const missing = programMemberships.find(
            (membership) =>
              !membership.endDate &&
              membership.isNew &&
              membership.isClinicianTrainee === true &&
              (membership.yearInStage == null ||
                (membership.yearInStage as unknown as string) === "")
          );
          return !missing || "Clinical Training Year in Program is required for trainees";
        },
        yearInStageRequiredForActiveTrainees: (programMemberships) => {
          const missing = programMemberships.find((membership, index) => {
            if (membership.endDate) return false;
            const isTrainee =
              membership.isClinicianTrainee ||
              (membership.programRole as unknown as ProgramRole) ===
                ProgramRole.Trainee;
            if (!isTrainee) return false;
            // If the program has no training_stage we can't enforce year — skip
            // here; that's a separate concern.
            if (!stageForMembership(membership, index)) return false;
            return (
              membership.yearInStage == null ||
              (membership.yearInStage as unknown as string) === ""
            );
          });
          return (
            !missing ||
            "Clinical Training Year in Program is not set. Please set this required field before saving."
          );
        },
        pgyRequiredForFellowTrainees: (programMemberships) => {
          // If any active trainee membership is on a Fellowship program, user.pgy must be set.
          const hasFellowTrainee = programMemberships.some(
            (membership, index) => {
              if (membership.endDate) return false;
              const isTrainee =
                membership.isClinicianTrainee ||
                (membership.programRole as unknown as ProgramRole) ===
                  ProgramRole.Trainee;
              if (!isTrainee) return false;
              return (
                stageForMembership(membership, index)?.code ===
                FELLOWSHIP_STAGE_CODE
              );
            }
          );
          const pgyValue = getValues("pgy");
          if (
            hasFellowTrainee &&
            (pgyValue == null || (pgyValue as unknown as string) === "")
          ) {
            return "PGY is required for fellowship trainees";
          }
          return true;
        },
        traineeCannotBePA: (programMemberships) => {
          const activeRoles = programMemberships
            .filter((m) => !m.endDate)
            .map((m) =>
              m.isClinicianTrainee ? ProgramRole.Trainee : m.programRole
            );
          const hasTrainee = activeRoles.includes(ProgramRole.Trainee);
          const hasProgramAdmin = activeRoles.includes(
            ProgramRole.ProgramAdmin
          );

          return (
            !(hasTrainee && hasProgramAdmin) ||
            UserFormErrors.TraineeCannotBeProgramAdmin
          );
        },
        // Only where this save adds the Coach / Rotation Director or the
        // Trainee row, so a legacy conflict elsewhere doesn't block
        // unrelated edits; the Lambda re-checks every program it touches.
        coachOrDirectorNotForTrainees: (programMemberships) => {
          const trainee = traineeProgramIds(programMemberships);
          const active = programMemberships.filter(
            (m) => !m.endDate && m.programId && trainee.has(m.programId)
          );
          return (
            !active.some(
              (m) =>
                isCoachOrDirectorRole(m.programRole) &&
                active.some(
                  (other) =>
                    other.programId === m.programId &&
                    (m.isNew || other.isNew) &&
                    effectiveRole(other) === ProgramRole.Trainee
                )
            ) || UserFormErrors.CoachOrDirectorNotForTrainees
          );
        }
      }
    }
  });

  watch("programMemberships");
  
  const transformMemberships = (
    programMemberships: MembershipPreview[]
  ): ProgramMembership[] => {
    return programMemberships.map((m) => {
      const role = m.programRole as NewProgramRole; // <-- key change

      let finalRole: ProgramRole;
      if (role === "Clinician") {
        finalRole = m.isClinicianTrainee
          ? ProgramRole.Trainee
          : ProgramRole.Attending;
      } else {
        finalRole = role; // Safe because it's already a ProgramRole
      }

      const { isNew, institutionId, isClinicianTrainee, ...rest } = m;

      return {
        ...rest,
        programRole: finalRole
      } as ProgramMembership;
    });
  };

  const onSubmit: SubmitHandler<EditUserInputs> = async (data) => {
    if (id) {
      const { email, programMemberships } = data;
      if (editingEmail) {
        if (email?.toLowerCase() === user?.email.toLowerCase()) {
          setError("email", {
            type: "custom",
            message: UserFormErrors.RequiresUniqueEmail
          });
        } else {
          const updateUserEmailInput: UpdateUserEmailArgs = {
            userId: user?.id,
            newEmail: email
          };
          const result = await updateUserEmail(updateUserEmailInput);
          if (result) {
            // Success: show toast, exit edit modes, and refresh user to reflect saved email
            dispatch(setToastMessage(UserNotifications.EmailUpdatedSuccessfully));
            dispatch(setToastStyle("success"));
            dispatch(setShowToast(true));
            setEditingEmail(false);
            setEditingUser(false);
            if (id) {
              getUserById(id);
            }
          }
        }
      } else {
        // Determine if this user has an active Fellowship Trainee membership;
        // only then is pgy editable via update-user (the lambda enforces this).
        const hasActiveFellowTraineeMembership = (data.programMemberships || []).some(
          (m, index) => {
            if (m.endDate) return false;
            const isTrainee =
              m.isClinicianTrainee ||
              (m.programRole as unknown as ProgramRole) === ProgramRole.Trainee;
            if (!isTrainee) return false;
            return (
              stageForMembership(m, index)?.code === FELLOWSHIP_STAGE_CODE
            );
          }
        );

        // Send only the shown identifiers that changed; the backend leaves the
        // rest untouched ("" clears).
        const storedIdentifiers = storedIdentifierValues(user);
        const identifiers: IdentifierValues = {};
        for (const item of identifierItems) {
          const value = String(data.identifiers?.[item.key] ?? "").trim();
          if (value !== (storedIdentifiers[item.key] ?? "")) {
            identifiers[item.key] = value;
          }
        }

        // 1) Update user first; if this fails (e.g., NPI conflict), do not save memberships
        const updateUserInput: UpdateUserInput = {
          id,
          firstName: data.firstName,
          lastName: data.lastName,
          ...(Object.keys(identifiers).length > 0 && { identifiers }),
          suffix: data.suffix,
          jobTitle: data.jobTitle,
          ...(hasActiveFellowTraineeMembership && { pgy: data.pgy })
        };
        const updatedUserResult = await updateUser(updateUserInput);
        if (!updatedUserResult) {
          // Inline NPI error will be set by existing effect; keep edit mode, do not save memberships
          return;
        }

        // 2) With user updated, update memberships. This was previously gated on
        // EULA acceptance — unregistered users couldn't end/add memberships
        // through the full membership path. SE-1722/SE-1725 lift that gate so Sys
        // Admins and Program Admins can end/add memberships for unregistered
        // users; the backend enforces per-program authorization. The yearInStage
        // write (step 3) remains separate (update-year-in-stage).
        {
          const transformed =
            transformMemberships(programMemberships) as unknown as ProgramMembership[];
          const confirmScopedAccessRemoval = confirmScopedRemovalRef.current;
          confirmScopedRemovalRef.current = false;
          const membershipResult = await updateMemberships(id, transformed, {
            confirmScopedAccessRemoval
          });
          if (!membershipResult.success) {
            if (
              membershipResult.errorCode === CONFIRM_SCOPED_ACCESS_REMOVAL &&
              membershipResult.removals.length > 0
            ) {
              // Memberships aren't saved yet; ask, then re-send (SE-3027).
              setPendingScopedRemovals(membershipResult.removals);
              return;
            }
            // Friendly copy: a 400 with an earliest-end-date is the SE-1724
            // involvement-floor rejection (tell the Sys Admin the earliest date
            // they may pick); a refusal with a code (SE-3027) already says what
            // happened; 403 means the caller doesn't administer one of the
            // programs; anything else points them at SIMPL Support (SE-1725).
            const membershipErrorMessage =
              membershipResult.errorStatus === 400 &&
              membershipResult.earliestEndDate
                ? ProgramMembershipMessages.EndDateBeforeFloor(
                    format(
                      new Date(`${membershipResult.earliestEndDate}T12:00:00`),
                      "PP"
                    )
                  )
                : membershipResult.errorCode && membershipResult.errorMessage
                  ? membershipResult.errorMessage
                  : membershipResult.errorStatus === 403
                    ? ProgramMembershipMessages.Forbidden
                    : ProgramMembershipMessages.GenericError;
            dispatch(setToastMessage(membershipErrorMessage));
            dispatch(setToastStyle("error"));
            dispatch(setShowToast(true));
            setError("programMemberships", {
              type: "server",
              message: membershipErrorMessage
            });
            return; // keep edit mode to correct memberships
          }
        }

        // 3) yearInStage updates on existing trainee memberships.
        // Runs for both registered and unregistered users so PAs can fill in
        // missing year_in_stage before the trainee accepts the EULA.
        const normalizeYear = (v: unknown): number | null => {
          if (v == null || v === "") return null;
          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) && Number.isInteger(n) ? n : null;
        };
        const originalMembershipsById = new Map(
          (user?.programMemberships?.items || [])
            .filter((m): m is NonNullable<typeof m> => !!m)
            .map((m) => [m.id, normalizeYear((m as any).yearInStage)])
        );
        const yearInStageUpdates = programMemberships
          .filter((m) => !m.isNew && m.id)
          .map((m) => ({
            membershipId: m.id as string,
            formValue: normalizeYear(m.yearInStage),
            originalValue: originalMembershipsById.get(m.id as string) ?? null
          }))
          .filter(
            ({ formValue, originalValue }) =>
              formValue !== null && formValue !== originalValue
          )
          .map(({ membershipId, formValue }) => ({
            membershipId,
            newValue: formValue as number
          }));

        if (yearInStageUpdates.length > 0) {
          try {
            const result = await updateYearInStage(yearInStageUpdates);
            if (result.skipped && result.skipped.length > 0) {
              const reasons = result.skipped
                .map((s) => s.reason)
                .join("; ");
              dispatch(
                setToastMessage(
                  `Some year-in-stage updates were skipped: ${reasons}`
                )
              );
              dispatch(setToastStyle("warning"));
              dispatch(setShowToast(true));
            }
          } catch (yearErr) {
            const message = `Error updating year in stage: ${yearErr}`;
            dispatch(setToastMessage(message));
            dispatch(setToastStyle("error"));
            dispatch(setShowToast(true));
            setError("programMemberships", {
              type: "server",
              message
            });
            return;
          }
        }

        // 3) All done; close edit mode and refresh state
        setEditingUser(false);
        // SE-1725: after adding a membership to an unregistered user, remind the
        // admin to set a usable email so the user can register. The `isNew`
        // guard is needed here because EditUser's save mixes edits and adds;
        // AddUserToProgram (which only ever adds) omits it deliberately.
        const addedMembershipToUnregistered =
          !user?.eulaAcceptedDate && programMemberships.some((m) => m.isNew);
        dispatch(
          setToastMessage(
            addedMembershipToUnregistered
              ? ProgramMembershipMessages.UnregisteredAddEmailReminder
              : UserNotifications.UserUpdatedSuccessfully
          )
        );
        dispatch(setToastStyle("success"));
        dispatch(setShowToast(true));
        if (id) {
          getUserById(id);
        }
      }
    }
  };

  const promptEndMembership = (index: number) => {
    setIndexOfMembershipToEnd(index);
    // Pre-fill with the membership's current end date when editing an already-
    // ended one (from Past Memberships); otherwise default to today (ending an
    // active membership).
    const existingEnd = getValues("programMemberships")?.[index]?.endDate;
    setEndDatePick(existingEnd ? new Date(existingEnd) : new Date());
    setEndDateError(null);
    setShowEndMembershipModal(true);
  };

  // SE-2798: open the confirm dialog to remove an ended membership's end date
  // (reactivate it).
  const promptRemoveEndDate = (index: number) => {
    setIndexToReactivate(index);
    setShowRemoveEndDateModal(true);
  };

  const setFormToStoredValues = (user: User) => {
    const {
      email,
      firstName,
      lastName,
      pgy,
      programMemberships,
      eulaAcceptedDate,
      suffix,
      jobTitle
    } = user;
    setRegistrationDate(
      eulaAcceptedDate ? format(new Date(eulaAcceptedDate), `PPPPpppp`) : "N/A"
    );
    // Need to revisit when a user can be archived and/or deleted.
    setUserStatus(eulaAcceptedDate ? "Active" : "Unregistered");
    let resolvedJobTitle = jobTitle;
    if (!resolvedJobTitle && Array.isArray(programMemberships?.items)) {
      const activeRoles = programMemberships?.items
        .filter(
          (pm) =>
            !pm?.endDate &&
            (pm?.programRole === ProgramRole.Trainee ||
              pm?.programRole === ProgramRole.Attending)
        )
        .map((pm) => pm?.programRole);

      if (activeRoles?.includes(ProgramRole.Trainee)) {
        resolvedJobTitle = "Trainee";
      } else if (activeRoles?.includes(ProgramRole.Attending)) {
        resolvedJobTitle = "Attending";
      } else {
        resolvedJobTitle = null;
      }
    }
    const flatMappedProgramMemberships = programMemberships?.items?.flatMap(
      (programMembership) => {
        if (!programMembership) return [];

        const transformedRole =
          programMembership.programRole === ProgramRole.Trainee ||
          programMembership.programRole === ProgramRole.Attending
            ? "Clinician"
            : programMembership.programRole;

        const isClinicianTrainee =
          programMembership.programRole === ProgramRole.Trainee;

        return [
          {
            ...programMembership,
            institutionId: programMembership.program?.institutionId,
            programTrainingStageId:
              (programMembership.program as any)?.trainingStageId ?? null,
            programRole: transformedRole,
            isClinicianTrainee,
            yearInStage: (programMembership as any).yearInStage ?? null
          }
        ];
      }
    ) as MembershipPreview[] || [];
    // Reset the entire form including the field array so isDirty is false on load
    reset(
      {
        email,
        firstName,
        lastName,
        pgy: (pgy as number | null) ?? null,
        identifiers: storedIdentifierValues(user),
        suffix,
        jobTitle: resolvedJobTitle,
        programMemberships: flatMappedProgramMemberships
      },
      {
        keepDirty: false,
        keepTouched: false,
        keepErrors: false
      }
    );
    setFormPopulated(true);
  };

  const [formPopulated, setFormPopulated] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [registrationDate, setRegistrationDate] = useState("");
  const [editingUser, setEditingUser] = useState(false);
  const [initialDataLoadComplete, setInitialDataLoadComplete] = useState(false);
  // True if any non-email field is dirty (supports nested dirtyFields)
  const hasNonEmailDirty = React.useMemo(() => {
    if (!isDirty) return false;
    const hasAnyNonEmailDirty = (df: Record<string, any>): boolean =>
      Object.entries(df || {}).some(([key, val]) => {
        if (key === "email") return false;
        if (val === true) return true;
        if (val && typeof val === "object") return hasAnyNonEmailDirty(val);
        return false;
      });
    return hasAnyNonEmailDirty(dirtyFields as unknown as Record<string, any>);
  }, [isDirty, dirtyFields]);

  const anyApiIsLoading = [
    status,
    updateUserStatus,
    updateMembershipsStatus,
    trainingStagesStatus
  ].includes("loading");
  const statusOfRequiredInitialData = [status, programsStatus];
  const loadingRequiredInitialData =
    statusOfRequiredInitialData.includes("loading");
  const errorLoadingRequiredInitialData =
    statusOfRequiredInitialData.includes("failed");
  const loadedRequiredInitialData = statusOfRequiredInitialData.every(
    (status) => status === "succeeded"
  );

  // Success handling is done inline in onSubmit after both updates succeed
  // If the email update failed on the backend (e.g., 409 conflict), surface it on the email field
  useEffect(() => {
    if (updateEmailStatus === "failed" && updateEmailError) {
      setError("email", {
        type: "server",
        message: updateEmailError
      });
      // Also show a toast for visibility
      dispatch(setToastMessage(updateEmailError));
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [updateEmailStatus, updateEmailError, setError]);

  useEffect(() => {
    if (updateUserStatus === "failed" && updateUserError) {
      const message = updateUserError;
      // Show the error on the identifier it names (e.g. "...this AAMC ID already exists").
      // Longest name first, so e.g. "AAMC ID" wins over a shorter "ID".
      const item = [...identifierItems]
        .sort((a, b) => b.displayName.length - a.displayName.length)
        .find((it) =>
          message.toLowerCase().includes(it.displayName.toLowerCase())
        );
      if (item) {
        setError(`identifiers.${item.key}`, {
          type: "server",
          message
        });
      }
      // Always show a toast for failed user update (including identifier conflicts)
      dispatch(setToastMessage(message));
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [
    updateUserStatus,
    updateUserError,
    dispatch,
    setError
  ]);

  // No separate membership error loop; onSubmit surfaces error inline and returns
  useEffect(() => {
    if (status === "succeeded" && user) {
      setFormToStoredValues(user);
    }
  }, [status, user]);

  useEffect(() => {
    if (loadedRequiredInitialData) setInitialDataLoadComplete(true);
  }, [loadedRequiredInitialData]);

  if (status === "idle") {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  if (loadingRequiredInitialData && !initialDataLoadComplete) {
    return (
      <PageWrapper {...pageDetails}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (errorLoadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error fetching user!")}</h2>
        <p>{error}</p>
      </PageWrapper>
    );
  }

  if (loadedRequiredInitialData && !user) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("User not found!")}</h2>
      </PageWrapper>
    );
  }

  // Dev Note: Need to account for this, especially for testing!
  if (loadedRequiredInitialData && user && !formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Populating form...")}</h2>
      </PageWrapper>
    );
  }

  const membershipType: ProgramRole =
    membershipFields.reduce<ProgramRole>((role, mf) => {
      if (mf.programRole == ProgramRole.Admin) {
        return ProgramRole.Admin;
      } else if (
        mf.programRole == ProgramRole.ProgramAdmin ||
        mf.programRole == ProgramRole.Attending
      ) {
        return ProgramRole.ProgramAdmin;
      } else {
        return ProgramRole.Trainee;
      }
    }, ProgramRole.Admin) ?? ProgramRole.Admin;

  const getProgramRoleSelectGroups = (membershipType: ProgramRole) => {
    if (membershipType == ProgramRole.Admin.toString()) {
      return ProgramRoleSelectFields.filter(
        (f) => f.value != ProgramRole.Admin
      );
    }
    return ProgramRoleSelectFields;
  };

  const programRoleSelectFields: { label: string; value: NewProgramRole }[] =
    getProgramRoleSelectGroups(membershipType) as {
      label: string;
      value: NewProgramRole;
    }[];

  // PGY is a user-level field, shown in Basic User Info only when the user has an
  // active Fellowship trainee membership. (A fellow's PGY can differ from their
  // year-in-program because of varied prior residency paths.)
  const isFellowUser = (watch("programMemberships") || []).some((m, index) => {
    if (m.endDate) return false;
    const isTrainee =
      m.isClinicianTrainee ||
      (m.programRole as unknown as ProgramRole) === ProgramRole.Trainee;
    if (!isTrainee) return false;
    return stageForMembership(m, index)?.code === FELLOWSHIP_STAGE_CODE;
  });

  const pgyChoices = Array.from(
    { length: PGY_MAX - PGY_MIN + 1 },
    (_, i) => PGY_MIN + i
  ).map((p) => ({ value: p, label: String(p) }));

  // SE-1724: a Sys Admin can't back-date an end before the membership started.
  const endTargetStartDate =
    indexOfMembershipToEnd !== undefined
      ? getValues("programMemberships")?.[indexOfMembershipToEnd]?.startDate
      : undefined;
  // SE-1724: opening the dialog from a Past Membership (one that already has an
  // end date) is an EDIT, not an end — drives the title/button copy.
  const editingEndedMembership =
    indexOfMembershipToEnd !== undefined &&
    !!getValues("programMemberships")?.[indexOfMembershipToEnd]?.endDate;

  const scopedRoleCopy = (role: ScopedAccessRemoval["programRole"]) =>
    role === "RotationDirector"
      ? { role: t("Rotation Director"), things: t("rotations") }
      : { role: t("Coach"), things: t("mentees") };

  // "Keep role": put back the end date the admin just set on each membership
  // the server asked about, so the form matches what is saved.
  const keepScopedRoles = () => {
    const removals = pendingScopedRemovals ?? [];
    setPendingScopedRemovals(null);
    const originalEndById = new Map(
      (user?.programMemberships?.items || [])
        .filter((m): m is NonNullable<typeof m> => !!m)
        .map((m) => [m.id, m.endDate ?? null])
    );
    const currentValues = getValues("programMemberships");
    replaceMemberships(
      currentValues.map((m) =>
        !m.isNew &&
        m.id &&
        !originalEndById.get(m.id) &&
        removals.some(
          (r) =>
            r.programId === m.programId &&
            (r.programRole as string) === (m.programRole as unknown as string)
        )
          ? { ...m, endDate: originalEndById.get(m.id) ?? null }
          : m
      )
    );
  };

  if (user && programs && formPopulated) {
    return (
      <PageWrapper {...pageDetails} noTopPadding>
        <Dialog
          open={pendingScopedRemovals !== null}
          aria-labelledby="remove-scoped-access-title"
          aria-describedby="remove-scoped-access-description"
        >
          <DialogTitle id="remove-scoped-access-title">
            {t("Remove assignments too?")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="remove-scoped-access-description">
              {t(
                "Ending this role also removes {{name}} from all of these assignments, along with the access they give. Program membership changes haven't been saved yet.",
                {
                  name:
                    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    user.email
                }
              )}
            </DialogContentText>
            {(pendingScopedRemovals ?? []).map((removal) => {
              const copy = scopedRoleCopy(removal.programRole);
              const programName =
                programs?.find((p) => p.id === removal.programId)?.name ??
                t("this program");
              return (
                <Box
                  key={`${removal.programId}-${removal.programRole}`}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="subtitle2">
                    {t("{{role}} in {{program}} — {{things}}:", {
                      role: copy.role,
                      program: programName,
                      things: copy.things
                    })}
                  </Typography>
                  <ul style={{ margin: 0 }}>
                    {removal.scopes.map((scope) => (
                      <li key={scope.scope_id}>
                        <Typography variant="body2">
                          {scope.scope_name || scope.scope_id}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={keepScopedRoles}>{t("Keep role")}</Button>
            <Button
              color="error"
              onClick={() => {
                setPendingScopedRemovals(null);
                confirmScopedRemovalRef.current = true;
                handleSubmit(onSubmit)();
              }}
            >
              {t("Remove and save")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showEndMembershipModal}
          aria-labelledby="end-membership-title"
          aria-describedby="end-membership-description"
        >
          <DialogTitle id="end-membership-title">
            {t(editingEndedMembership ? "Edit End Date" : "End Program Membership")}
          </DialogTitle>
          <DialogContent>
            {(() => {
              // SE-3027: name what is ending (whose role, where), and mention
              // assessments only for the roles that create or complete them
              // (Trainee / Attending). Shown to EVERYONE.
              const ending =
                indexOfMembershipToEnd === undefined
                  ? undefined
                  : getValues("programMemberships")[indexOfMembershipToEnd];
              const endingProgram = programs?.find(
                (p) => p.id === ending?.programId
              );
              const where = [
                endingProgram?.institution?.name,
                endingProgram?.name
              ]
                .filter(Boolean)
                .join(" · ");
              const role = ending ? effectiveRole(ending) : undefined;
              const assesses =
                role === ProgramRole.Trainee || role === ProgramRole.Attending;
              // They leave the program's User list only with their last
              // active membership there.
              const lastInProgram =
                !!ending &&
                !getValues("programMemberships").some(
                  (m, i) =>
                    i !== indexOfMembershipToEnd &&
                    m.programId === ending.programId &&
                    !m.endDate
                );
              return (
                <DialogContentText id="end-membership-description">
                  {t(
                    "Are you sure you want to end {{name}}'s {{role}} membership in {{where}}?",
                    {
                      name:
                        [user.firstName, user.lastName]
                          .filter(Boolean)
                          .join(" ") || user.email,
                      role: ending ? getRoleDisplay(ending).label : "",
                      where: where || t("this program")
                    }
                  )}{" "}
                  {/* SE-1724: Sys Admins pick the end date below (today or
                      earlier), so only promise "today" to everyone else. */}
                  {!isSysAdmin && `${t("The end date will be today's date.")} `}
                  {assesses &&
                    `${t(
                      "They will no longer be able to create or complete assessments in this program."
                    )} `}
                  {lastInProgram &&
                    `${t(
                      "They will no longer show up in the User list of this program."
                    )} `}
                  {t("This action cannot be undone.")}
                </DialogContentText>
              );
            })()}
            {isSysAdmin ? (
              <Box mt={2}>
                {/* minDate only bounds the membership's start date; the
                    assessment-involvement floor is server-enforced (we don't
                    have it client-side) and returns a 400 with earliestEndDate. */}
                <DatePicker
                  label={t("End date")}
                  value={endDatePick}
                  maxDate={new Date()}
                  minDate={
                    endTargetStartDate
                      ? new Date(endTargetStartDate)
                      : undefined
                  }
                  onChange={(d) => setEndDatePick(d)}
                  onError={(reason) => setEndDateError(reason)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            ) : (
              <Box
                mt={1}
                display="flex"
                alignItems="center"
                gap={0.5}
                data-testid="end-membership-support-note"
              >
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {t("Need an earlier end date? Contact SIMPL Support at ")}
                  <Link
                    href={`mailto:help@simpl.org?subject=${encodeURIComponent(
                      "SIMPL Support- Change End Date of a Program Membership"
                    )}`}
                    underline="hover"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    help@simpl.org
                  </Link>
                  .
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowEndMembershipModal(false);
                setIndexOfMembershipToEnd(undefined);
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              disabled={
                indexOfMembershipToEnd === undefined ||
                (isSysAdmin && (!endDatePick || !!endDateError))
              }
              onClick={() => {
                // Defensive: the button is disabled on an invalid date, but guard
                // the click too (a typed future date fires onChange before onError
                // flushes) so a bad value can never reach the form.
                if (isSysAdmin && (!endDatePick || endDateError)) {
                  return;
                }
                if (indexOfMembershipToEnd !== undefined) {
                  const currentValues = getValues("programMemberships");
                  const entryInMembershipFields =
                    currentValues[indexOfMembershipToEnd];
                  const updatedValues = currentValues;
                  // Sys Admins end as of the picked day; everyone else = now.
                  // toEndDateIso is the ONE place that resolves this: the actual
                  // current instant for "today" (noon-UTC reads as a back-date to
                  // the server's gate and 403s non-SysAdmins), noon-UTC only for a
                  // genuinely back-dated (past) pick so it renders on that day.
                  const endDate =
                    isSysAdmin && endDatePick
                      ? toEndDateIso(endDatePick)
                      : new Date().toISOString();
                  updatedValues[indexOfMembershipToEnd] = {
                    ...entryInMembershipFields,
                    endDate
                  };
                  replaceMemberships(updatedValues);
                  // SE-1724: the "Edit end date" action on a Past Membership is
                  // available OUTSIDE edit mode, so enter edit mode now — the
                  // Save button only renders when editingUser is true, and the
                  // change the user just made is otherwise unsaveable.
                  setEditingUser(true);
                  setShowEndMembershipModal(false);
                }
              }}
            >
              {t(editingEndedMembership ? "Update End Date" : "End Membership")}
            </Button>
          </DialogActions>
        </Dialog>
        {/* SE-2798: remove an ended membership's end date (reactivate). */}
        <Dialog
          open={showRemoveEndDateModal}
          onClose={() => setShowRemoveEndDateModal(false)}
          aria-labelledby="remove-end-date-title"
        >
          <DialogTitle id="remove-end-date-title">
            {t("Remove End Date")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                "Remove the end date for this program membership? This reactivates the membership — the User returns to this program's active User list, rosters, and reporting. Save your changes to apply."
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowRemoveEndDateModal(false);
                setIndexToReactivate(undefined);
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              disabled={indexToReactivate === undefined}
              onClick={() => {
                if (indexToReactivate !== undefined) {
                  const currentValues = getValues("programMemberships");
                  const updatedValues = currentValues;
                  updatedValues[indexToReactivate] = {
                    ...currentValues[indexToReactivate],
                    endDate: null
                  };
                  replaceMemberships(updatedValues);
                  // Reveal Save for the change just made (same as the end flow).
                  setEditingUser(true);
                  setShowRemoveEndDateModal(false);
                  setIndexToReactivate(undefined);
                }
              }}
            >
              {t("Remove End Date")}
            </Button>
          </DialogActions>
        </Dialog>
        <form
          className="EditUserForm"
          id="edit-user-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Grid
            container
            spacing={2}
            p={1}
            sx={{ flex: 1, minHeight: 0, overflowY: "scroll" }}
          >
            <Grid xs={12}>
              <fieldset>
                <legend>{t("Basic User Info")}</legend>
                <Grid xs={12} container p={1}>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="firstName"
                      rules={{ required: UserFormErrors.EmptyFirstName }}
                      label="First Name"
                      required={editingUser}
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                    {errors.firstName && (
                      <Alert severity="error">
                        {t(UserFormErrors.EmptyFirstName)}
                      </Alert>
                    )}
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="lastName"
                      rules={{ required: UserFormErrors.EmptyLastName }}
                      label="Last Name"
                      required={editingUser}
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                    {errors.lastName && (
                      <Alert severity="error">
                        {t(UserFormErrors.EmptyLastName)}
                      </Alert>
                    )}
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="suffix"
                      label="Suffix"
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <ReactHookFormTextField
                      control={control}
                      fieldName="email"
                      rules={{
                        required: UserFormErrors.EmptyEmail,
                        pattern: {
                          value: Regex.Email,
                          message: UserFormErrors.InvalidEmail
                        }
                      }}
                      label="Email Address"
                      required={editingUser}
                      disabled={
                        !editingEmail || updateEmailStatus === "loading"
                      }
                    />
                    {errors.email && (
                      <Alert severity="error">
                        {t(errors.email.message || "")}
                      </Alert>
                    )}
                    {editingUser && (
                      <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                        {!editingEmail && (
                          <Button
                            variant="contained"
                            disabled={anyApiIsLoading || hasNonEmailDirty}
                            onClick={() => {
                              setEditingEmail(true);
                            }}
                          >
                            {t("Edit Email")}
                          </Button>
                        )}
                        {editingEmail && (
                          <>
                            <LoadingButton
                              type="submit"
                              variant="contained"
                              loading={updateEmailStatus === "loading"}
                            >
                              {t("Save")}
                            </LoadingButton>
                            <Button
                              disabled={updateEmailStatus === "loading"}
                              variant="outlined"
                              onClick={() => {
                                // Revert only the email field and clear its errors; do not reset the whole form
                                setValue("email", user?.email ?? null, {
                                  shouldDirty: false,
                                  shouldValidate: false
                                });
                                clearErrors("email");
                                setEditingEmail(false);
                              }}
                            >
                              {t("Cancel")}
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {identifierConfigLoading || identifierConfigFailed ? (
                    <IdentifierConfigStatus
                      status={identifierConfigLoading ? "loading" : "failed"}
                      onRetry={retryIdentifierConfig}
                    />
                  ) : (
                    <IdentifierFields
                      items={identifierItems}
                      control={control}
                      errors={errors}
                      disabled={anyApiIsLoading || editingEmail || !editingUser}
                      skipValidation={editingEmail}
                      gridProps={{ xs: 12, md: 6 }}
                    />
                  )}

                  <Grid xs={12} md={6}>
                    <TextField
                      label={t("Status")}
                      value={userStatus}
                      sx={{ width: "100%" }}
                      disabled
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      label={t("Registration Date")}
                      value={registrationDate}
                      sx={{ width: "100%" }}
                      disabled
                    />
                  </Grid>
                  {(getValues("programMemberships") || []).some((m) => {
                    const role = m.programRole as NewProgramRole;
                    return (
                      role === "Clinician" ||
                      role === ProgramRole.Trainee ||
                      role === ProgramRole.Attending
                    );
                  }) && (
                    <Grid xs={12} md={6}>
                      <ReactHookFormTextField
                        control={control}
                        fieldName="jobTitle"
                        rules={{ required: UserFormErrors.JobTitleRequired }}
                        label="Job Title"
                        required={editingUser}
                        disabled={
                          anyApiIsLoading || editingEmail || !editingUser
                        }
                      />
                      {errors.jobTitle && (
                        <Alert severity="error">
                          {t(UserFormErrors.JobTitleRequired)}
                        </Alert>
                      )}
                    </Grid>
                  )}
                  {isFellowUser && (
                    <Grid xs={12} md={6}>
                      <ReactHookFormSelectField
                        control={control}
                        fieldName="pgy"
                        label="PGY"
                        required={editingUser}
                        disabled={
                          anyApiIsLoading || editingEmail || !editingUser
                        }
                        choices={pgyChoices}
                        trigger={trigger}
                      />
                    </Grid>
                  )}
                </Grid>
              </fieldset>
            </Grid>
            <Grid xs={12}>
              <fieldset>
                <legend>{t("User Memberships")}</legend>
                <Grid xs={12} container p={1} spacing={2}>
                  <Grid xs={12}>
                    {(() => {
                      const all = (getValues("programMemberships") || []).map(
                        (membershipField, originalIndex) => ({
                          membershipField,
                          formIndex: originalIndex
                        })
                      );
                      const pastMemberships = all
                        .filter(({ membershipField }) => !!membershipField.endDate)
                        .sort(
                          (a, b) =>
                            new Date(
                              b.membershipField.endDate || 0
                            ).valueOf() -
                            new Date(a.membershipField.endDate || 0).valueOf()
                        );
                      const activeMemberships = all
                        .filter(({ membershipField }) => !membershipField.endDate)
                        .sort(
                          (a, b) =>
                            new Date(
                              a.membershipField.startDate || 0
                            ).valueOf() -
                            new Date(
                              b.membershipField.startDate || 0
                            ).valueOf()
                        );

                      return (
                        <Stack spacing={2}>
                          {pastMemberships.length > 0 && (
                            <Accordion
                              disableGutters
                              sx={{
                                backgroundColor: "background.default",
                                boxShadow: "none",
                                border: "1px solid",
                                borderColor: "divider"
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ minHeight: 40 }}
                              >
                                <Typography variant="body2">
                                  {t("Past Memberships")} (
                                  {pastMemberships.length})
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0 }}>
                                <Stack divider={<Box sx={{ borderTop: "1px dashed", borderColor: "divider" }} />}>
                                  {pastMemberships.map(
                                    ({ membershipField, formIndex }) => {
                                      const membershipProgram = programs?.find(
                                        (p) => p.id === membershipField.programId
                                      );
                                      return (
                                        <PastMembershipRow
                                          key={membershipField.id ?? formIndex}
                                          membershipField={membershipField}
                                          membershipProgramName={
                                            membershipProgram?.name ?? ""
                                          }
                                          membershipInstitutionName={
                                            membershipProgram?.institution
                                              ?.name ?? ""
                                          }
                                          stage={stageForMembership(
                                            membershipField,
                                            formIndex
                                          )}
                                          isSysAdmin={isSysAdmin}
                                          onEditEndDate={() =>
                                            promptEndMembership(formIndex)
                                          }
                                          onRemoveEndDate={() =>
                                            promptRemoveEndDate(formIndex)
                                          }
                                        />
                                      );
                                    }
                                  )}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {activeMemberships.length > 0 && (() => {
                            // Split into existing (saved) and new memberships.
                            // New memberships always render as the inline form
                            // regardless of view mode — the form has dropdowns
                            // that don't fit cleanly in a table cell. The view
                            // toggle only affects how existing memberships are
                            // displayed.
                            const existingActive = activeMemberships.filter(
                              ({ membershipField }) => !membershipField.isNew
                            );
                            const newActive = activeMemberships.filter(
                              ({ membershipField }) => membershipField.isNew
                            );

                            return (
                              <Stack spacing={1.5}>
                                {existingActive.length > 0 && (
                                  <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                  >
                                    <ToggleButtonGroup
                                      size="small"
                                      exclusive
                                      value={membershipsViewMode}
                                      onChange={(_, value) => {
                                        if (value) setMembershipsViewMode(value);
                                      }}
                                      aria-label={t("Memberships view mode")}
                                    >
                                      <ToggleButton
                                        value="cards"
                                        aria-label={t("Cards view")}
                                      >
                                        <Tooltip title={t("Cards view")}>
                                          <ViewModuleIcon fontSize="small" />
                                        </Tooltip>
                                      </ToggleButton>
                                      <ToggleButton
                                        value="table"
                                        aria-label={t("Table view")}
                                      >
                                        <Tooltip title={t("Table view")}>
                                          <ViewListIcon fontSize="small" />
                                        </Tooltip>
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                  </Stack>
                                )}

                                {existingActive.length > 0 &&
                                  membershipsViewMode === "cards" && (
                                    <Stack spacing={1.5}>
                                      {existingActive.map(
                                        ({ membershipField, formIndex }) => {
                                          const membershipProgram =
                                            programs?.find(
                                              (p) =>
                                                p.id ===
                                                membershipField.programId
                                            );
                                          const stage = stageForMembership(
                                            membershipField,
                                            formIndex
                                          );
                                          const userAdministersThisProgram =
                                            !!programsCurrentUserCanAdminister?.find(
                                              (p) =>
                                                p.id ===
                                                membershipField.programId
                                            );
                                          return (
                                            <ActiveMembershipCard
                                              key={membershipField.id}
                                              membershipField={membershipField}
                                              formIndex={formIndex}
                                              membershipProgramName={
                                                membershipProgram?.name ?? ""
                                              }
                                              membershipInstitutionName={
                                                membershipProgram?.institution
                                                  ?.name ?? ""
                                              }
                                              stage={stage}
                                              control={control}
                                              setValue={setValue}
                                              trigger={trigger}
                                              editingUser={editingUser}
                                              anyApiIsLoading={anyApiIsLoading}
                                              editingEmail={editingEmail}
                                              isUserCurrentlyAdministeringProgram={
                                                userAdministersThisProgram
                                              }
                                              onEndMembership={
                                                promptEndMembership
                                              }
                                            />
                                          );
                                        }
                                      )}
                                    </Stack>
                                  )}

                                {existingActive.length > 0 &&
                                  membershipsViewMode === "table" && (
                                    <TableContainer
                                      component={Paper}
                                      variant="outlined"
                                    >
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ width: 36 }} />
                                            <TableCell>{t("Role")}</TableCell>
                                            <TableCell>
                                              {t("Institution")}
                                            </TableCell>
                                            <TableCell>
                                              {t("Program")}
                                            </TableCell>
                                            <TableCell>
                                              {t("Trainee Type")}
                                            </TableCell>
                                            <TableCell>
                                              {t("Clinical Training Year in Program")}
                                            </TableCell>
                                            <TableCell>
                                              {t("Start Date")}
                                            </TableCell>
                                            <TableCell align="right" />
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {existingActive.map(
                                            ({ membershipField, formIndex }) => {
                                              const membershipProgram =
                                                programs?.find(
                                                  (p) =>
                                                    p.id ===
                                                    membershipField.programId
                                                );
                                              const stage = stageForMembership(
                                                membershipField,
                                                formIndex
                                              );
                                              const userAdministersThisProgram =
                                                !!programsCurrentUserCanAdminister?.find(
                                                  (p) =>
                                                    p.id ===
                                                    membershipField.programId
                                                );
                                              return (
                                                <ActiveMembershipTableRow
                                                  key={membershipField.id}
                                                  membershipField={
                                                    membershipField
                                                  }
                                                  formIndex={formIndex}
                                                  membershipProgramName={
                                                    membershipProgram?.name ??
                                                    ""
                                                  }
                                                  membershipInstitutionName={
                                                    membershipProgram
                                                      ?.institution?.name ?? ""
                                                  }
                                                  stage={stage}
                                                  control={control}
                                                  setValue={setValue}
                                                  trigger={trigger}
                                                  editingUser={editingUser}
                                                  anyApiIsLoading={
                                                    anyApiIsLoading
                                                  }
                                                  editingEmail={editingEmail}
                                                  isUserCurrentlyAdministeringProgram={
                                                    userAdministersThisProgram
                                                  }
                                                  onEndMembership={
                                                    promptEndMembership
                                                  }
                                                />
                                              );
                                            }
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}

                                {newActive.length > 0 && (
                                  <Stack spacing={1.5}>
                                    {newActive.map(
                                      ({ membershipField, formIndex }) => {
                                        const stage = stageForMembership(
                                          membershipField,
                                          formIndex
                                        );
                                        return (
                                          <NewMembershipForm
                                            key={`new-${formIndex}`}
                                            membershipField={membershipField}
                                            formIndex={formIndex}
                                            control={control}
                                            getValues={getValues}
                                            setValue={setValue}
                                            trigger={trigger}
                                            isFormBusy={
                                              anyApiIsLoading || editingEmail
                                            }
                                            institutionsCurrentUserCanAdminister={
                                              institutionsCurrentUserCanAdminister ??
                                              []
                                            }
                                            getProgramsInInstitution={
                                              getProgramsInInstitution
                                            }
                                            stage={stage}
                                            programRoleSelectFields={
                                              programRoleSelectFields
                                            }
                                            onRemove={removeMembership}
                                          />
                                        );
                                      }
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            );
                          })()}

                          {activeMemberships.length === 0 &&
                            pastMemberships.length === 0 && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 2, textAlign: "center" }}
                              >
                                {t("No memberships yet.")}
                              </Typography>
                            )}
                        </Stack>
                      );
                    })()}
                  </Grid>
                  {errors.programMemberships && (
                    <Grid>
                      <Alert severity="error">
                        {/* The save path sets the field-level message
                            (setError("programMemberships", …)); show it. */}
                        {t(
                          errors.programMemberships.message ||
                            errors.programMemberships.root?.message ||
                            "Program Membership Error"
                        )}
                      </Alert>
                    </Grid>
                  )}
                  {editingUser && (
                    <Grid xs={12}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={
                          editingEmail ||
                          anyApiIsLoading ||
                          // TODO: Make this based on logged-in user's ProgramMemberships
                          // Disable the button if there aren't any
                          // programs in the user's institution
                          !programsCurrentUserCanAdminister ||
                          programsCurrentUserCanAdminister.length < 1
                        }
                        onClick={() => {
                          if (id) {
                            const institutionId =
                              institutionsCurrentUserCanAdminister?.length == 1
                                ? institutionsCurrentUserCanAdminister[0]?.id
                                : "";
                            const programs =
                              getProgramsInInstitution(institutionId);
                            const programId =
                              programs?.length == 1 ? programs[0]?.id : "";
                            const newMembership: MembershipPreview = {
                              id: generateUUID(),
                              __typename: "ProgramMembership",
                              programMemberId: id,
                              // No role assumed — the user must pick one explicitly.
                              programRole: "" as unknown as ProgramRole,
                              institutionId,
                              programId,
                              isNew: true,
                              startDate: new Date().toISOString()
                            };
                            appendMembership(newMembership);
                          }
                        }}
                      >
                        {t("Add Membership")}
                      </Button>
                    </Grid>
                  )}
                  {editingUser && !user.eulaAcceptedDate && (
                    <Grid xs={12}>
                      <Alert severity="info">
                        {t(
                          ProgramMembershipMessages.UnregisteredAddEmailReminder
                        )}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            p={1}
            sx={{ backgroundColor: "background.paper", zIndex: 1 }}
          >
            {!editingUser && (
              <Grid>
                <Button
                  disabled={anyApiIsLoading || editingEmail}
                  onClick={() => setEditingUser(true)}
                  variant="contained"
                >
                  {t("Edit User")}
                </Button>
              </Grid>
            )}
            {editingUser && (
              <>
                <Grid>
                  <LoadingButton
                    disabled={
                      !isDirty ||
                      anyApiIsLoading ||
                      editingEmail ||
                      !!errors.programMemberships ||
                      identifierConfigLoading ||
                      identifierConfigFailed
                    }
                    loading={
                      updateUserStatus === "loading" ||
                      updateMembershipsStatus === "loading"
                    }
                    type="submit"
                    variant="contained"
                  >
                    {t("Save")}
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Button
                    onClick={() => {
                      setEditingUser(false);
                      setFormToStoredValues(user);
                    }}
                    disabled={anyApiIsLoading}
                    variant="outlined"
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </PageWrapper>
    );
  }
  // Null return to satisfy typescript requirements
  return null;
};
